import { useStore } from 'zustand';
import { useQueries } from '@tanstack/react-query';
import { getMunicipalitiesByMunicipalityIdRolesOptions } from '@citylytics/core-api-ts-client/react-query';
import { LIST_PAGE_MAX_SIZE } from '@citylytics/http/constants';
import { PERMISSION_ACTION } from '@citylytics/domain/permissions/constants';
import { hasPermission, holdsAllPermissions } from '@citylytics/domain/permissions/utils';
import { isProtectedRole } from '@citylytics/domain/roles/utils';
import { selectWholeMunicipalityRoles } from '@citylytics/web-api-client/roles/utils';
import { useMunicipalitiesLookup } from '@citylytics/web-api-client/municipalities/hooks';
import { useGlobalRolesQuery, useMunicipalityPermissionsQuery, useMunicipalityRoleMutations, useMunicipalityRolesQuery, } from '@citylytics/web-api-client/roles/hooks';
// Each lists both forms, as the routes' `access` does: staff and the municipality's own users.
const ROLES_READ_ACCESS = [
    PERMISSION_ACTION.ROLES_READ_GLOBAL,
    PERMISSION_ACTION.ROLES_READ_MUNICIPALITY,
];
const ROLES_CREATE_ACCESS = [
    PERMISSION_ACTION.ROLES_CREATE_GLOBAL,
    PERMISSION_ACTION.ROLES_CREATE_MUNICIPALITY,
];
const ROLES_UPDATE_ACCESS = [
    PERMISSION_ACTION.ROLES_UPDATE_GLOBAL,
    PERMISSION_ACTION.ROLES_UPDATE_MUNICIPALITY,
];
const ROLES_DELETE_ACCESS = [
    PERMISSION_ACTION.ROLES_DELETE_GLOBAL,
    PERMISSION_ACTION.ROLES_DELETE_MUNICIPALITY,
];
const PERMISSIONS_GRANT_ACCESS = [
    PERMISSION_ACTION.PERMISSIONS_GRANT_GLOBAL,
    PERMISSION_ACTION.PERMISSIONS_GRANT_MUNICIPALITY,
];
/**
 * Whether the signed-in user may perform an action, by the same rule core-api's `restrictTo`
 * applies (`hasPermission`), so the UI only offers what the API would accept. Pass a route's
 * `access` list as `actions`.
 */
function useCurrentUserPermissions(store) {
    const user = useStore(store, (state) => state.user);
    function can(check) {
        if (!user) {
            return false;
        }
        return hasPermission({ user, actions: check.actions, municipalityId: check.municipalityId });
    }
    return { can };
}
/**
 * Reloads the signed-in user's permissions after a change that may touch them. A failure is
 * told to the user through `onRefreshFailed`, who can reload the page, rather than leaving stale
 * gates unnoticed; the change itself already succeeded, so it isn't rethrown into the mutation.
 */
function useRefreshCurrentUser(params) {
    const { refreshCurrentUser, onRefreshFailed } = params;
    return async function refresh() {
        try {
            await refreshCurrentUser();
        }
        catch {
            onRefreshFailed();
        }
    };
}
/** A global user needs a global role, a municipality user one of that municipality's roles. */
function useRoleOptions(params) {
    return useScopeRoles(params).map((role) => ({ value: role.id, label: role.name }));
}
/** The roles a user in this place may hold, with their permissions; cached per scope. */
function useScopeRoles(params) {
    const { store, municipalityId } = params;
    const { can } = useCurrentUserPermissions(store);
    const isGlobal = municipalityId === null;
    const globalRoles = useGlobalRolesQuery({
        enabled: isGlobal && can({ actions: [PERMISSION_ACTION.ROLES_READ_GLOBAL] }),
    });
    // A list rather than one query, so a global user runs none instead of one keyed by a fake id.
    const [municipalityRoles] = useQueries({
        queries: (isGlobal ? [] : [municipalityId]).map((id) => ({
            ...getMunicipalitiesByMunicipalityIdRolesOptions({
                path: { municipalityId: id },
                query: { size: LIST_PAGE_MAX_SIZE },
            }),
            select: selectWholeMunicipalityRoles,
            enabled: can({ actions: ROLES_READ_ACCESS, municipalityId: id }),
        })),
    });
    const roles = isGlobal ? globalRoles.data : municipalityRoles?.data;
    return roles ?? [];
}
/**
 * Whether the signed-in user holds every permission of the role `roleId`, the API's rule for
 * acting on someone with that role. Until the role has loaded the answer is no, so controls never
 * flash on and then off.
 */
function useHoldsRole(params) {
    const { store, roleId, municipalityId } = params;
    const currentUser = useStore(store, (state) => state.user);
    const roles = useScopeRoles({ store, municipalityId });
    const role = roles.find(({ id }) => id === roleId);
    return (currentUser !== null &&
        role !== undefined &&
        holdsAllPermissions({
            user: currentUser,
            actions: role.permissions.map(({ action }) => action),
        }));
}
/**
 * Whether the signed-in user may change another user, by the API's rule on top of the route's
 * `access` (`updateAccess`): never yourself, and only someone whose current role holds nothing
 * you lack.
 */
function useCanManageUser(params) {
    const { store, target, updateAccess } = params;
    const { can } = useCurrentUserPermissions(store);
    const currentUser = useStore(store, (state) => state.user);
    const holdsRole = useHoldsRole({
        store,
        roleId: target.role.id,
        municipalityId: target.municipalityId,
    });
    const isSelf = currentUser?.id === target.id;
    return (!isSelf &&
        holdsRole &&
        can({ actions: updateAccess, municipalityId: target.municipalityId ?? undefined }));
}
/**
 * What the signed-in user may do to a role, by the same rules the API applies on top of the route's
 * `access`: Admin is never changed, a role is only changed by someone holding all its permissions,
 * and only permissions the user holds can be given. The UI then never offers a save or delete the
 * API refuses, and the picker leaves out what the save would be refused for.
 */
function useRoleEditGates(params) {
    const { store, role, updateAccess, grantAccess, deleteAccess, municipalityId } = params;
    const { can } = useCurrentUserPermissions(store);
    const user = useStore(store, (state) => state.user);
    const holdsRole = user !== null &&
        holdsAllPermissions({ user, actions: role.permissions.map(({ action }) => action) });
    const isManageable = holdsRole && !isProtectedRole(role);
    function filterGrantable(options) {
        if (!user) {
            return [];
        }
        // The role's own permissions stay visible even when the user lacks them, so nothing it holds
        // silently drops out of the grid.
        const roleIds = new Set(role.permissions.map(({ id }) => id));
        return options.filter((option) => roleIds.has(option.id) || holdsAllPermissions({ user, actions: [option.action] }));
    }
    return {
        canEditPermissions: isManageable &&
            can({ actions: grantAccess, municipalityId }) &&
            can({ actions: updateAccess, municipalityId }),
        canDelete: isManageable && can({ actions: deleteAccess, municipalityId }),
        filterGrantable,
    };
}
/**
 * A municipality's roles, and whether the signed-in user may read and create them; the list is
 * only fetched for someone who may read it.
 */
function useMunicipalityRoles(params) {
    const { store, municipalityId } = params;
    const { can } = useCurrentUserPermissions(store);
    const canRead = can({ actions: ROLES_READ_ACCESS, municipalityId });
    const roles = useMunicipalityRolesQuery({ municipalityId, enabled: canRead });
    return { roles, canRead, canCreate: can({ actions: ROLES_CREATE_ACCESS, municipalityId }) };
}
/**
 * One municipality role's card: what the user may do to it, what its permission grid offers, and
 * its save and delete wired to `mutations`, ready for ui-web's `RoleEditorCard` once the app adds
 * its text. The choices are the municipality's ceiling, not the catalogue, fetched only for
 * someone who may edit, and narrowed to what the user may grant.
 */
function useMunicipalityRoleEditor(params) {
    const { store, municipalityId, role, mutations } = params;
    const gates = useRoleEditGates({
        store,
        role,
        updateAccess: ROLES_UPDATE_ACCESS,
        grantAccess: PERMISSIONS_GRANT_ACCESS,
        deleteAccess: ROLES_DELETE_ACCESS,
        municipalityId,
    });
    const available = useMunicipalityPermissionsQuery({
        municipalityId,
        enabled: gates.canEditPermissions,
    });
    const path = { municipalityId, roleId: role.id };
    return {
        deletion: gates.canDelete
            ? {
                isDeleting: mutations.remove.isPending,
                onDelete: () => mutations.remove.mutateAsync({ path }),
            }
            : undefined,
        // Waits for what may be granted rather than grow into it; null when the user may only look.
        grantable: gates.canEditPermissions ? available : null,
        toOptions: (grantable) => grantable ? gates.filterGrantable(grantable) : role.permissions,
        editor: {
            savedIds: role.permissions.map(({ id }) => id),
            canEdit: gates.canEditPermissions,
            save: mutations.update,
            onSave: (permissionIds) => mutations.update.mutate({ path, body: { permissionIds } }),
        },
    };
}
/**
 * Every municipality, for internal staff to name or pick one; empty without the global read, so
 * a municipality's own users never ask for a list they may not see.
 */
function useMunicipalityLookup(store) {
    const { can } = useCurrentUserPermissions(store);
    return useMunicipalitiesLookup({
        enabled: can({ actions: [PERMISSION_ACTION.MUNICIPALITIES_READ_GLOBAL] }),
    });
}
/**
 * The hooks above, bound once to an app's session, as `createSessionService` is: each app calls
 * this in its `lib/session/session.singleton.ts` and components import the result from there.
 * `userUpdateAccess` is the route's `access` the app changes users through (global for staff's
 * `/users`, both forms for `/municipalities/{id}/users`); `notify` shows what the hooks report.
 */
export function createPermissionHooks(params) {
    const { store, refreshCurrentUser, userUpdateAccess, notify } = params;
    function useRefresh() {
        return useRefreshCurrentUser({
            refreshCurrentUser,
            onRefreshFailed: () => notify({ kind: 'refresh-failed' }),
        });
    }
    function useRoleMutations(municipalityId) {
        const refresh = useRefresh();
        return useMunicipalityRoleMutations({
            municipalityId,
            onChanged: refresh,
            onSaved: () => notify({ kind: 'role-saved' }),
        });
    }
    return {
        useCurrentUserPermissions: () => useCurrentUserPermissions(store),
        useRefreshCurrentUser: useRefresh,
        useRoleOptions: (municipalityId) => useRoleOptions({ store, municipalityId }),
        useHoldsRole: (role) => useHoldsRole({ store, ...role }),
        useCanManageUser: (target) => useCanManageUser({ store, target, updateAccess: userUpdateAccess }),
        useRoleEditGates: (gates) => useRoleEditGates({ store, ...gates }),
        useMunicipalityRoles: (municipalityId) => useMunicipalityRoles({ store, municipalityId }),
        useMunicipalityRoleMutations: useRoleMutations,
        useMunicipalityRoleEditor: (role) => useMunicipalityRoleEditor({
            store,
            ...role,
            mutations: useRoleMutations(role.municipalityId),
        }),
        useMunicipalityLookup: () => useMunicipalityLookup(store),
    };
}
